import * as React from "react"

import { Skeleton } from "@ibada/ui"

import type { MoneyString } from "@ibada/shared-types/finance"

import { BudgetHealth, BudgetScope } from "@/features/budgeting/model/budget-enums"
import { useBudgetPerformanceQuery } from "@/features/budgeting/services/budgets-query"

import { ProgressBar, type ProgressTone } from "@/features/finance/overview/components/progress-bar"
import {
  useFinanceOverviewPeriod,
  useFinanceOverviewScope,
} from "@/features/finance/overview/context/finance-overview-context"
import { formatMoneySafe, formatPercent } from "@/features/finance/overview/lib/overview-format"

/**
 * Shared plumbing for the five IBA-157 budget performance widgets.
 *
 * They all read ONE endpoint, and they all render figures the server computed. Nothing here derives a
 * percentage, sums a column, or converts money to a float — that is the point. A widget that recomputed
 * would eventually disagree with the budget page about the same number, and the user would have no way to
 * tell which one was lying.
 */
export function useBudgetPerformance() {
  const scope = useFinanceOverviewScope()
  const period = useFinanceOverviewPeriod()

  return useBudgetPerformanceQuery(
    {
      workspaceId: scope.workspaceId ?? undefined,
      context: scope.context === "parish" ? BudgetScope.Parish : BudgetScope.Branch,
      branchId: scope.branchId ?? undefined,
      dateFrom: period.from,
      dateTo: period.to,
    },
    { enabled: Boolean(scope.workspaceId && scope.branchId && period.from && period.to) },
  )
}

/** Server-computed health -> progress-bar tone. Never re-derived here. */
export function toneForHealth(health: BudgetHealth): ProgressTone {
  switch (health) {
    case BudgetHealth.Healthy:
      return "success"
    case BudgetHealth.Warning:
      return "warning"
    case BudgetHealth.Critical:
      return "destructive"
    default:
      return "muted"
  }
}

export type PerformanceRow = {
  id: string
  label: string
  planned: MoneyString
  actual: MoneyString
  /** Null when nothing was planned. Rendered as a dash, NOT as 0% — there is no plan to be at 0% of. */
  utilizationPct: number | null
  tone: ProgressTone
  /** Optional second line, e.g. a fund's available balance. */
  footnote?: string
}

/** The shared planned-vs-actual row list: label, utilization, bar, and the two amounts underneath. */
export function PerformanceRowList({ rows, currency }: { rows: PerformanceRow[]; currency: string }) {
  return (
    <ul className="space-y-3">
      {rows.map((row) => (
        <li key={row.id} className="space-y-1.5">
          <div className="flex items-baseline justify-between gap-3">
            <span className="truncate text-sm font-medium text-foreground">{row.label}</span>
            <span className="shrink-0 text-xs font-medium tabular-nums text-muted-foreground">
              {row.utilizationPct === null ? "—" : formatPercent(row.utilizationPct)}
            </span>
          </div>
          <ProgressBar value={row.utilizationPct ?? 0} tone={row.tone} aria-label={row.label} />
          <p className="text-xs tabular-nums text-muted-foreground">
            {formatMoneySafe(row.actual, currency)} / {formatMoneySafe(row.planned, currency)}
            {row.footnote ? <span className="ml-2 opacity-80">{row.footnote}</span> : null}
          </p>
        </li>
      ))}
    </ul>
  )
}

export function PerformanceRowsSkeleton() {
  return (
    <ul className="space-y-3">
      {[0, 1, 2].map((index) => (
        <li key={index} className="space-y-1.5">
          <div className="flex justify-between">
            <Skeleton className="h-3.5 w-28" />
            <Skeleton className="h-3.5 w-8" />
          </div>
          <Skeleton className="h-1.5 w-full rounded-full" />
          <Skeleton className="h-3 w-24" />
        </li>
      ))}
    </ul>
  )
}

export function PerformanceTableSkeleton() {
  return (
    <div className="space-y-2">
      {[0, 1, 2, 3].map((index) => (
        <div key={index} className="flex items-center gap-3">
          <Skeleton className="h-3.5 w-1/3" />
          <Skeleton className="h-3.5 w-1/4" />
          <Skeleton className="h-3.5 w-1/6" />
          <Skeleton className="h-3.5 w-1/6" />
        </div>
      ))}
    </div>
  )
}
